import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../store";
import {
  getUsers,
  setPage,
  setLimit,
  setFilters,
  setViewMode,
  deleteUser,
  clearError,
} from "../../features/users/usersSlice";
import {
  Button,
  ButtonGroup,
  Card,
  Col,
  Collapse,
  Container,
  Form,
  Pagination,
  Row,
  Table,
  Badge,
  Dropdown,
  Modal,
  Spinner,
} from "react-bootstrap";
import { FaUsers, FaList, FaTh, FaPlus, FaFileExcel } from "react-icons/fa";
import { Icon } from "../../components/common/Icon";
import { toast } from "react-toastify";
import UserFormModal from "../../components/users/UserFormModal";
import UserCard from "./components/UserCard";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import ErrorAlert from "../../components/common/ErrorAlert";
import ConfirmationModal from "../../components/common/ConfirmationModal";
import { Role, User } from "../../features/users/usersSlice";
import rolesService from "../../features/roles/rolesService";
import companiesService from "../../features/companies/companiesService";
import usersService from "../../features/users/usersService";
import { Company } from "../../features/companies/companiesService";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../context/ThemeContext";
import { canManageUsers } from "../../utils/permissions";
import BrandGlyph from "../../components/common/BrandGlyph";

const UsersPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    users,
    isLoading,
    error,
    totalCount,
    page,
    limit,
    filters,
    viewMode,
  } = useAppSelector((state) => state.users);
  const { user } = useAppSelector((state) => state.auth);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme === "dark";

  // State for forms and modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // State for filters
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState<number | undefined>(undefined);
  const [companyFilter, setCompanyFilter] = useState<number | undefined>(
    undefined
  );
  const [filterExpanded, setFilterExpanded] = useState(false);

  // State for roles and companies (for filters and forms)
  const [roles, setRoles] = useState<Role[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [exportLoading, setExportLoading] = useState(false);

  // Load users on component mount and when filters change
  useEffect(() => {
    dispatch(getUsers({ page, limit, ...filters }));
  }, [dispatch, page, limit, filters]);

  // Load roles and companies for filters
  useEffect(() => {
    const loadFiltersData = async () => {
      try {
        const rolesData = await rolesService.getRoles();
        setRoles(rolesData);

        const companiesData = await companiesService.getCompanies({
          is_active: true,
        });
        setCompanies(companiesData.companies);
      } catch (error) {
        console.error("Error loading filters data:", error);
        toast.error("Failed to load filter options");
      }
    };

    loadFiltersData();
  }, []);

  // Handle filter changes
  const handleFilterChange = () => {
    const newFilters = {
      ...filters,
      status: statusFilter || undefined,
      role_id: roleFilter,
      company_id: companyFilter,
      search: searchTerm || undefined,
    };
    dispatch(setFilters(newFilters));
  };

  // Handle sorting
  const handleSort = (field: string) => {
    const isAsc = filters.sort_by === field && filters.sort_order === "asc";
    const newFilters = {
      ...filters,
      sort_by: field,
      sort_order: (isAsc ? "desc" : "asc") as "asc" | "desc",
    };
    dispatch(setFilters(newFilters));
  };

  const getSortIcon = (field: string) => {
    if (filters.sort_by !== field) {
      return <i className="bi bi-arrow-down-up ms-2 text-muted" style={{ fontSize: '0.8rem' }}></i>;
    }
    return filters.sort_order === "asc" ? (
      <i className="bi bi-sort-up ms-2 text-primary"></i>
    ) : (
      <i className="bi bi-sort-down ms-2 text-primary"></i>
    );
  };

  // Reset filters
  const handleResetFilters = () => {
    setSearchTerm("");
    setStatusFilter("");
    setRoleFilter(undefined);
    setCompanyFilter(undefined);
    dispatch(setFilters({
      sort_by: 'created_at',
      sort_order: 'desc'
    }));
  };

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    dispatch(setPage(newPage));
  };

  // Handle rows per page change
  const handleLimitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    dispatch(setLimit(Number(e.target.value)));
    dispatch(setPage(1)); // Reset to first page
  };

  // Handle view mode change
  const handleViewModeChange = (mode: "list" | "grid") => {
    dispatch(setViewMode(mode));
  };

  // Export users to Excel (uses current filters)
  const handleExportUsers = async () => {
    setExportLoading(true);
    try {
      await usersService.exportUsers({
        status: statusFilter || undefined,
        role_id: roleFilter,
        company_id: companyFilter,
        search: searchTerm || undefined,
      });
      toast.success(t("export_success") || "Export completed successfully.");
    } catch (err: any) {
      toast.error(err.response?.data?.detail || t("export_failed") || "Export failed.");
    } finally {
      setExportLoading(false);
    }
  };

  // Open create modal
  const handleOpenCreateModal = () => {
    setShowCreateModal(true);
  };

  // Close create modal
  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
  };

  // Open edit modal
  const handleOpenEditModal = (user: User) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  // Close edit modal
  const handleCloseEditModal = () => {
    setSelectedUser(null);
    setShowEditModal(false);
  };

  // Open delete modal
  const handleOpenDeleteModal = (user: User) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  // Close delete modal
  const handleCloseDeleteModal = () => {
    setSelectedUser(null);
    setShowDeleteModal(false);
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (searchTerm) count++;
    if (statusFilter) count++;
    if (roleFilter) count++;
    if (companyFilter) count++;
    return count;
  };

  // Filter card styles (match CompaniesFilter)
  const filterCardStyle: React.CSSProperties = {
    background: isDark ? "var(--bg-elevated)" : "white",
    border: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(0, 0, 0, 0.05)",
    boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.2)" : "0 4px 20px rgba(0, 0, 0, 0.03)",
    marginBottom: "24px",
    borderRadius: "20px",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    overflow: "visible",
  };
  const filterHeaderStyle: React.CSSProperties = {
    background: filterExpanded
      ? (isDark ? "var(--bg-elevated)" : "white")
      : "transparent",
    border: "none",
    borderBottom: filterExpanded ? (isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(0,0,0,0.05)") : "none",
    padding: "16px 24px",
    cursor: "pointer",
    borderRadius: filterExpanded ? "20px 20px 0 0" : "20px",
    transition: "all 0.3s ease",
    position: "relative",
    zIndex: 2,
  };
  const filterIconContainerStyle: React.CSSProperties = {
    width: "36px",
    height: "36px",
    background: isDark ? "rgba(59, 130, 246, 0.2)" : "rgba(59, 130, 246, 0.1)",
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: "12px",
    transition: "all 0.3s ease",
    color: "var(--brand-primary)",
  };
  const filterTitleStyle: React.CSSProperties = {
    margin: "0",
    fontWeight: "600",
    fontSize: "16px",
    color: isDark ? "#f1f5f9" : "#0f172a",
    lineHeight: "1.4",
  };
  const filterSubtitleStyle: React.CSSProperties = {
    color: "#3b82f6",
    fontSize: "12px",
    fontWeight: "500",
    marginTop: "2px",
  };
  const filterLabelStyle: React.CSSProperties = {
    fontWeight: "600",
    color: isDark ? "#cbd5e1" : "#374151",
    marginBottom: "8px",
    fontSize: "14px",
  };
  const filterInputStyle: React.CSSProperties = {
    height: "48px",
    borderRadius: "12px",
    border: isDark ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(0, 0, 0, 0.1)",
    background: isDark ? "rgba(51, 65, 85, 0.8)" : "rgba(248, 250, 252, 0.8)",
    color: isDark ? "#f1f5f9" : "#0f172a",
    fontSize: "14px",
    fontWeight: "500",
  };
  const filterButtonStyle = (variant: "primary" | "outline") =>
    variant === "primary"
      ? {
          padding: "10px 24px",
          borderRadius: "10px",
          border: "none",
          fontWeight: "600",
          fontSize: "14px",
          background: "linear-gradient(135deg, var(--brand-primary) 0%, #7c3aed 100%)",
          color: "white",
          boxShadow: "0 4px 12px rgba(59, 130, 246, 0.2)",
        }
      : {
          padding: "10px 24px",
          borderRadius: "10px",
          fontWeight: "600",
          fontSize: "14px",
          background: isDark ? "#334155" : "#f1f5f9",
          color: isDark ? "#94a3b8" : "#64748b",
          border: "1px solid " + (isDark ? "#475569" : "#e2e8f0"),
        };

  // Handle delete user
  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      await dispatch(deleteUser(selectedUser.id)).unwrap();
      toast.success("User deleted successfully");
      handleCloseDeleteModal();
    } catch (error) {
      console.error("Failed to delete user:", error);
      toast.error("Failed to delete user");
    }
  };

  // Get status badge
  const getStatusBadge = (status: string) => {
    const baseClasses = "users-status-badge d-inline-flex align-items-center";

    switch (status.toLowerCase()) {
      case "active":
        return (
          <Badge className={`${baseClasses} users-status-active`}>
            <i className="bi bi-check-circle-fill me-1"></i>
            {t("active")}
          </Badge>
        );
      case "inactive":
        return (
          <Badge className={`${baseClasses} users-status-inactive`}>
            <i className="bi bi-pause-circle-fill me-1"></i>
            {t("inactive")}
          </Badge>
        );
      case "suspended":
        return (
          <Badge className={`${baseClasses} users-status-suspended`}>
            <i className="bi bi-exclamation-triangle-fill me-1"></i>
            {t("suspended")}
          </Badge>
        );
      case "deleted":
        return (
          <Badge className={`${baseClasses} users-status-deleted`}>
            <i className="bi bi-trash-fill me-1"></i>
            {t("deleted")}
          </Badge>
        );
      default:
        return (
          <Badge className={`${baseClasses} users-status-default`}>
            <i className="bi bi-info-circle-fill me-1"></i>
            {status}
          </Badge>
        );
    }
  };

  const getRoleBadges = (userRoles: string[]) => {
    return userRoles.map((roleKey) => (
      <Badge key={roleKey} className="users-role-badge me-1">
        <i className="bi bi-person-badge-fill me-1"></i>
        {roleKey}
      </Badge>
    ));
  };

  // getCompanyBadges fonksiyonu şöyle olmalı:
  const getCompanyBadges = (
    userCompanies: { company_id: number; role?: string }[]
  ) => {
    return userCompanies
      .map((userCompany) => {
        const company = companies.find((c) => c.id === userCompany.company_id);
        return company ? (
          <Badge key={company.id} className="users-company-badge me-1">
            <i className="bi bi-building-fill me-1"></i>
            {company.company_name}
          </Badge>
        ) : null;
      })
      .filter(Boolean);
  };

  // Calculate pagination (same logic as Companies: current ±2, ellipsis)
  const totalPages = totalCount > 0 ? Math.ceil(totalCount / limit) : 1;
  const paginationItems = [];

  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= page - 2 && i <= page + 2)) {
      paginationItems.push(
        <Pagination.Item
          key={i}
          active={i === page}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>
      );
    } else if (i === page - 3 || i === page + 3) {
      paginationItems.push(<Pagination.Ellipsis key={i} disabled />);
    }
  }

  const activeCount = users.filter((u) => u.status === "active").length;

  return (
    <div className="users-page">
      <div className="users-page-container">
        {/* Modern Header */}
        <div className="modern-header slide-in-down mb-4">
          <div className="header-content">
            <div className="header-left">
              <div className="header-icon-wrapper">
                <div className="header-icon">
                  <Icon icon={FaUsers} size="1.5rem" className="text-white" />
                </div>
              </div>
              <div className="header-text">
                <h1 className="header-title">{t("user_management")}</h1>
                <p className="header-subtitle">
                  {totalCount} {t("total")} {t("users")} • {activeCount} {t("active")}
                </p>
              </div>
            </div>
            <div className="header-actions">
              <ButtonGroup size="sm" className="companies-view-toggle me-2">
                <Button
                  variant={viewMode === "list" ? "primary" : "outline-secondary"}
                  onClick={() => handleViewModeChange("list")}
                  className="view-toggle-btn"
                  aria-label={t("list_view") || "List view"}
                >
                  <Icon icon={FaList} size="1rem" />
                </Button>
                <Button
                  variant={viewMode === "grid" ? "primary" : "outline-secondary"}
                  onClick={() => handleViewModeChange("grid")}
                  className="view-toggle-btn"
                  aria-label={t("grid_view") || "Grid view"}
                >
                  <Icon icon={FaTh} size="1rem" />
                </Button>
              </ButtonGroup>
              <Button
                variant="primary"
                size="sm"
                className="btn-modern btn-primary me-2"
                onClick={handleExportUsers}
                disabled={exportLoading}
              >
                {exportLoading ? (
                  <Spinner size="sm" className="me-1" />
                ) : (
                  <Icon icon={FaFileExcel} className="me-1" />
                )}
                <span>{t("export")}</span>
              </Button>
              <Button
                variant="primary"
                className="btn-modern btn-primary"
                onClick={handleOpenCreateModal}
              >
                <Icon icon={FaPlus} className="me-2" />
                <span>{t("add_user")}</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Filters - same design as Companies (single Card, no outer container) */}
        <Card style={filterCardStyle}>
          <Card.Header
            style={filterHeaderStyle}
            onClick={() => setFilterExpanded(!filterExpanded)}
            onMouseEnter={(e) => {
              if (!filterExpanded) {
                e.currentTarget.style.background = isDark
                  ? "rgba(51, 65, 85, 0.3)"
                  : "rgba(248, 250, 252, 0.5)";
              }
            }}
            onMouseLeave={(e) => {
              if (!filterExpanded) e.currentTarget.style.background = "transparent";
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center" }}>
                <div style={filterIconContainerStyle}>
                  <i className="bi bi-funnel-fill" />
                </div>
                <div>
                  <h6 style={filterTitleStyle}>{t("filters")}</h6>
                  {getActiveFiltersCount() > 0 && (
                    <small style={filterSubtitleStyle}>
                      {getActiveFiltersCount()} {t("active_filters")}
                    </small>
                  )}
                </div>
              </div>
              <i
                className="bi bi-chevron-down"
                style={{
                  color: isDark ? "#64748b" : "#9ca3af",
                  fontSize: "14px",
                  transition: "all 0.3s ease",
                  transform: filterExpanded ? "rotate(180deg)" : "rotate(0deg)",
                }}
              />
            </div>
          </Card.Header>

          <Collapse in={filterExpanded}>
            <div style={{ position: "relative", zIndex: 1 }}>
              <Card.Body style={{ paddingTop: "16px", paddingLeft: "24px", paddingRight: "24px", paddingBottom: "24px" }}>
                <Row className="g-4 mb-4">
                  {/* Search Field */}
                  <Col lg={4} md={6} xs={12}>
                    <Form.Group>
                      <Form.Label style={filterLabelStyle}>{t("search")}</Form.Label>
                      <div style={{ position: "relative" }}>
                        <Form.Control
                          type="text"
                          placeholder={t("search_placeholder")}
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          style={{ ...filterInputStyle, paddingLeft: "48px" }}
                        />
                        <i className="bi bi-search" style={{ position: "absolute", left: "16px", top: "50%", transform: "translateY(-50%)", color: isDark ? "#64748b" : "#9ca3af", fontSize: "16px", pointerEvents: "none" }} />
                        {searchTerm && (
                          <Button variant="link" size="sm" onClick={() => setSearchTerm("")} style={{ position: "absolute", right: "8px", top: "50%", transform: "translateY(-50%)", padding: "0 4px" }}>
                            <i className="bi bi-x-lg" />
                          </Button>
                        )}
                      </div>
                    </Form.Group>
                  </Col>

                  {/* Status Filter */}
                  <Col lg={2} md={6} xs={6}>
                    <Form.Group>
                      <Form.Label style={filterLabelStyle}>{t("status")}</Form.Label>
                      <Form.Select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        style={filterInputStyle}
                      >
                        <option value="">{t("all_statuses")}</option>
                        <option value="active">{t("active")}</option>
                        <option value="inactive">{t("inactive")}</option>
                        <option value="suspended">{t("suspended")}</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  {/* Role Filter */}
                  <Col lg={2} md={6} xs={6}>
                    <Form.Group>
                      <Form.Label style={filterLabelStyle}>{t("role")}</Form.Label>
                      <Form.Select
                        value={roleFilter || ""}
                        onChange={(e) => setRoleFilter(e.target.value ? Number(e.target.value) : undefined)}
                        style={filterInputStyle}
                      >
                        <option value="">{t("all_roles")}</option>
                        {roles.map((role) => (
                          <option key={role.id} value={role.id}>{role.name}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  {/* Company Filter */}
                  <Col lg={3} md={6} xs={12}>
                    <Form.Group>
                      <Form.Label style={filterLabelStyle}>{t("company")}</Form.Label>
                      <Form.Select
                        value={companyFilter || ""}
                        onChange={(e) => setCompanyFilter(e.target.value ? Number(e.target.value) : undefined)}
                        style={filterInputStyle}
                      >
                        <option value="">{t("all_companies")}</option>
                        {companies.map((company) => (
                          <option key={company.id} value={company.id}>{company.company_name}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Action Buttons - same layout as Companies */}
                <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px", flexWrap: "wrap" }}>
                  <Button
                    variant="outline-secondary"
                    onClick={handleResetFilters}
                    disabled={getActiveFiltersCount() === 0}
                    style={{ ...filterButtonStyle("outline"), opacity: getActiveFiltersCount() === 0 ? 0.5 : 1, cursor: getActiveFiltersCount() === 0 ? "not-allowed" : "pointer" }}
                  >
                    <i className="bi bi-arrow-clockwise me-2" />
                    {t("reset_all")}
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleFilterChange}
                    disabled={isLoading}
                    style={filterButtonStyle("primary")}
                  >
                    {isLoading ? <Spinner size="sm" className="me-2" /> : <i className="bi bi-check2-circle me-2" />}
                    {t("apply")}
                  </Button>
                </div>

                {/* Active Filters Display */}
                {getActiveFiltersCount() > 0 && (
                  <div className="users-active-filters mt-3 pt-3" style={{ borderTop: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(0,0,0,0.06)" }}>
                    <div className="users-active-filters-label mb-2" style={{ fontSize: "13px", fontWeight: 600, color: isDark ? "#cbd5e1" : "#374151" }}>
                      <i className="bi bi-funnel me-2" />
                      {t("active_filters")}:
                    </div>
                    <div className="users-active-filters-list d-flex flex-wrap gap-2">
                      {searchTerm && (
                        <span className="users-filter-tag users-filter-tag-search" style={{ display: "inline-flex", alignItems: "center", gap: "4px", padding: "4px 10px", borderRadius: "8px", background: isDark ? "rgba(59,130,246,0.2)" : "rgba(59,130,246,0.1)", fontSize: "13px" }}>
                          <i className="bi bi-search" /> "{searchTerm}"
                          <button type="button" className="users-filter-tag-remove border-0 bg-transparent p-0 ms-1" style={{ cursor: "pointer" }} onClick={() => setSearchTerm("")}><i className="bi bi-x" /></button>
                        </span>
                      )}
                      {statusFilter && (
                        <span className="users-filter-tag users-filter-tag-status" style={{ display: "inline-flex", alignItems: "center", gap: "4px", padding: "4px 10px", borderRadius: "8px", background: isDark ? "rgba(59,130,246,0.2)" : "rgba(59,130,246,0.1)", fontSize: "13px" }}>
                          <i className="bi bi-toggle2-on" /> {t(statusFilter)}
                          <button type="button" className="border-0 bg-transparent p-0 ms-1" style={{ cursor: "pointer" }} onClick={() => setStatusFilter("")}><i className="bi bi-x" /></button>
                        </span>
                      )}
                      {roleFilter && (
                        <span className="users-filter-tag users-filter-tag-role" style={{ display: "inline-flex", alignItems: "center", gap: "4px", padding: "4px 10px", borderRadius: "8px", background: isDark ? "rgba(59,130,246,0.2)" : "rgba(59,130,246,0.1)", fontSize: "13px" }}>
                          <i className="bi bi-person-badge" /> {roles.find((r) => r.id === roleFilter)?.name}
                          <button type="button" className="border-0 bg-transparent p-0 ms-1" style={{ cursor: "pointer" }} onClick={() => setRoleFilter(undefined)}><i className="bi bi-x" /></button>
                        </span>
                      )}
                      {companyFilter && (
                        <span className="users-filter-tag users-filter-tag-company" style={{ display: "inline-flex", alignItems: "center", gap: "4px", padding: "4px 10px", borderRadius: "8px", background: isDark ? "rgba(59,130,246,0.2)" : "rgba(59,130,246,0.1)", fontSize: "13px" }}>
                          <i className="bi bi-building" /> {companies.find((c) => c.id === companyFilter)?.company_name}
                          <button type="button" className="border-0 bg-transparent p-0 ms-1" style={{ cursor: "pointer" }} onClick={() => setCompanyFilter(undefined)}><i className="bi bi-x" /></button>
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </Card.Body>
            </div>
          </Collapse>
        </Card>

        {/* Error display */}
        {error && (
          <ErrorAlert message={error} onClose={() => dispatch(clearError())} />
        )}

        {/* Loading indicator */}
        {isLoading && (
          <LoadingSpinner 
            size={60}
            text="Loading Users"
            subtitle="Fetching user data..."
            className="loading-in-card"
          />
        )}

        {/* Users list view */}
        {!isLoading && viewMode === "list" && (
          <Card className="users-table-card slide-in-up animate-delay-2">
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table className="users-table mb-0">
                  <thead className="users-table-header">
                    <tr>
                      <th className="users-table-th" onClick={() => handleSort("full_name")} style={{ cursor: 'pointer' }}>
                        <div className="users-th-content">
                          <i className="bi bi-person me-2"></i>
                          {t("name")}
                          {getSortIcon("full_name")}
                        </div>
                      </th>
                      <th className="users-table-th users-table-th-email" onClick={() => handleSort("email")} style={{ cursor: 'pointer' }}>
                        <div className="users-th-content">
                          <i className="bi bi-envelope me-2"></i>
                          {t("email")}
                          {getSortIcon("email")}
                        </div>
                      </th>
                      <th className="users-table-th" onClick={() => handleSort("status")} style={{ cursor: 'pointer' }}>
                        <div className="users-th-content">
                          <i className="bi bi-toggle2-on me-2"></i>
                          {t("status")}
                          {getSortIcon("status")}
                        </div>
                      </th>
                      <th className="users-table-th">
                        <div className="users-th-content">
                          <i className="bi bi-person-badge me-2"></i>
                          {t("roles")}
                        </div>
                      </th>
                      <th className="users-table-th" onClick={() => handleSort("created_at")} style={{ cursor: 'pointer' }}>
                        <div className="users-th-content">
                          <i className="bi bi-calendar-event me-2"></i>
                          {t("created_at")}
                          {getSortIcon("created_at")}
                        </div>
                      </th>
                      <th className="users-table-th text-end">
                        <div className="users-th-content justify-content-end">
                          <i className="bi bi-gear me-2"></i>
                          {t("actions")}
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="users-table-body">
                    {users.length > 0 ? (
                      users.map((user, index) => (
                        <tr
                          key={user.id}
                          className={`users-table-row ${index % 2 === 0 ? "users-row-even" : ""}`}
                        >
                          <td className="users-table-cell">
                            <div className="users-user-info">
                              <div className="users-user-avatar">
                                <div className="users-avatar-circle">
                                  <span className="users-avatar-text">
                                    {user.full_name?.charAt(0)?.toUpperCase() ||
                                      "U"}
                                  </span>
                                </div>
                                <div className="users-avatar-status"></div>
                              </div>
                              <div className="users-user-details">
                                <div className="users-user-name">
                                  {user.full_name}
                                </div>
                                <div className="users-user-meta">
                                  <span className="users-user-id">ID: {user.id}</span>
                                  {user.username && (
                                    <span className="users-user-username">
                                      <i className="bi bi-at me-1" />
                                      {user.username}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="users-table-cell users-table-cell-email">
                            <div className="users-email-display">
                              <i className="bi bi-envelope me-2"></i>
                              {user.email}
                            </div>
                          </td>
                          <td className="users-table-cell">
                            {getStatusBadge(user.status)}
                          </td>
                          <td className="users-table-cell">
                            <div className="users-roles-container">
                              {getRoleBadges(user.roles)}
                            </div>
                          </td>
                          <td className="users-table-cell">
                            <div className="users-date-display">
                              <div className="users-date-primary">
                                {new Date(user.created_at).toLocaleDateString()}
                              </div>
                              <div className="users-date-secondary">
                                {new Date(user.created_at).toLocaleTimeString()}
                              </div>
                            </div>
                          </td>
                          <td className="users-table-cell text-end">
                            <div className="users-action-buttons">
                              <Button
                                size="sm"
                                className="users-action-btn users-action-btn-profile"
                                onClick={() => navigate(`/users/${user.id}`)}
                                title={t("profile") || "View profile"}
                              >
                                <i className="bi bi-person-badge"></i>
                              </Button>
                              <Button
                                size="sm"
                                className="users-action-btn users-action-btn-edit"
                                onClick={() => handleOpenEditModal(user)}
                                title={t("edit_user")}
                              >
                                <i className="bi bi-pencil-square"></i>
                              </Button>
                              <Button
                                size="sm"
                                className="users-action-btn users-action-btn-delete"
                                onClick={() => handleOpenDeleteModal(user)}
                                title={t("delete_user")}
                              >
                                <i className="bi bi-trash3"></i>
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={7} className="users-empty-state">
                          <div className="users-empty-content">
                            <div className="users-empty-icon">
                              <BrandGlyph size={80} animated={false} />
                            </div>
                            <h5 className="users-empty-title">
                              {t("no_users_found")}
                            </h5>
                            <p className="users-empty-description">
                              {t("no_users_description")}
                            </p>
                            <Button
                              onClick={handleOpenCreateModal}
                              className="btn-standard btn-primary"
                            >
                              <i className="bi bi-plus-circle"></i>
                              {t("add_first_user")}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        )}

        {/* Users grid view */}
        {!isLoading && viewMode === "grid" && (
          <div className="users-grid slide-in-up animate-delay-2">
            {users.length > 0 ? (
              <Row className="g-4">
                {users.map((user) => (
                  <Col key={user.id} xl={3} lg={4} md={6} sm={12}>
                    <UserCard
                      user={user}
                      onEdit={() => handleOpenEditModal(user)}
                      onDelete={() => handleOpenDeleteModal(user)}
                      onProfile={() => navigate(`/users/${user.id}`)}
                      getStatusBadge={getStatusBadge}
                      getRoleBadges={getRoleBadges}
                    />
                  </Col>
                ))}
              </Row>
            ) : (
              <div className="users-grid-empty-state">
                <div className="users-empty-content">
                  <div className="users-empty-icon">
                    <BrandGlyph size={80} animated={false} />
                  </div>
                  <h5 className="users-empty-title">{t("no_users_found")}</h5>
                  <p className="users-empty-description">
                    {t("no_users_description")}
                  </p>
                  <Button
                    onClick={handleOpenCreateModal}
                    className="btn-standard btn-primary"
                  >
                    <i className="bi bi-plus-circle"></i>
                    {t("add_first_user")}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Pagination - same structure as Companies */}
        {!isLoading && totalCount > 0 && (
          <div className="pagination-footer-premium mt-4 d-flex flex-wrap align-items-center justify-content-between gap-3">
            <div className="d-flex align-items-center gap-3">
              <div className="rows-select-wrapper d-flex align-items-center gap-2">
                <span className="text-muted small">{t("rows_per_page")}:</span>
                <Form.Select
                  size="sm"
                  value={limit}
                  onChange={handleLimitChange}
                  className="form-select-premium"
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                </Form.Select>
              </div>
              <div className="pagination-text-info text-muted small">
                {t("showing")} <span className="fw-bold text-primary">{(page - 1) * limit + 1}</span>-
                <span className="fw-bold text-primary">{Math.min(page * limit, totalCount)}</span> /
                <span className="fw-bold text-primary"> {totalCount}</span>
              </div>
            </div>

            <Pagination className="pagination-premium mb-0">
              <Pagination.First
                onClick={() => handlePageChange(1)}
                disabled={page === 1}
              />
              <Pagination.Prev
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
              />
              {paginationItems}
              <Pagination.Next
                onClick={() => handlePageChange(page + 1)}
                disabled={page === totalPages}
              />
              <Pagination.Last
                onClick={() => handlePageChange(totalPages)}
                disabled={page === totalPages}
              />
            </Pagination>
          </div>
        )}

        {/* User Form Modal */}
        <UserFormModal
          show={showCreateModal || showEditModal}
          onHide={(refresh) => {
            if (refresh) {
              dispatch(getUsers({ page, limit, ...filters }));
            }
            handleCloseCreateModal();
            handleCloseEditModal();
          }}
          user={selectedUser}
          roles={roles}
          companies={companies}
        />

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          show={showDeleteModal}
          onCancel={handleCloseDeleteModal}
          onConfirm={handleDeleteUser}
          title={t("delete_user")}
          message={`${t("are_you_sure_you_want_to_delete")} ${selectedUser?.full_name}? ${t("this_action_cannot_be_undone")}`}
          confirmText={t("delete")}
        />
      </div>
    </div>
  );
};

export default UsersPage;
